import React, { useEffect, useState } from "react";
import axios from "axios";
import { FiArrowUpRight, FiRefreshCw, FiArrowRight, FiArrowLeft } from "react-icons/fi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANG_COLORS = {
  Python: "#3572A5", "Jupyter Notebook": "#DA5B0B", TSQL: "#e38c00",
  "Power Query": "#f2c811", SQL: "#e38c00", JavaScript: "#f1e05a", null: "#9ca3af",
};

export default function ProjectsSection({ horizontalProgress = 0 }) {
  const [repos, setRepos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1280
  );

  async function loadRepos(refresh = false) {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/github/repos${refresh ? "?refresh=true" : ""}`);
      setRepos(data);
    } catch (e) {
      console.error("repos load failed", e);
    } finally { setLoading(false); }
  }
  useEffect(() => { loadRepos(true); }, []);

  // Keep the layout in sync with the real viewport (resize/orientation
  // change), instead of freezing whatever window.innerWidth happened to be
  // on first render.
  useEffect(() => {
    function onResize() { setViewportWidth(window.innerWidth); }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isMobile = viewportWidth < 768;

  // Compute translate based on external horizontal progress (0..1).
  // Desktop keeps the wide left-text / right-cards split; mobile uses a
  // near-edge-to-edge single-card carousel so cards aren't clipped.
  const gap = isMobile ? 16 : 24;
  const cardWidth = isMobile ? Math.max(240, viewportWidth - 64) : 460;
  const viewportOffset = isMobile ? 24 : viewportWidth * 0.42;
  const trackWidth = repos.length * (cardWidth + gap);
  const maxTranslate = Math.max(0, trackWidth - (viewportWidth - viewportOffset));
  const tx = -horizontalProgress * maxTranslate;
  const activeIdx = Math.min(repos.length - 1, Math.round(horizontalProgress * (repos.length - 1)));

  return (
    <section className="snap-section" data-testid="projects-section"
      style={{ background: "var(--paper)", color: "var(--ink)" }}>

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 flex justify-between items-center px-6 md:px-16 pt-24 pb-4 z-10">
        <span className="font-mono text-[10px] tracking-[0.35em] uppercase text-[var(--muted)]">Projects · GitHub Live</span>
        <div className="flex items-center gap-3">
          <button data-testid="projects-refresh" onClick={() => loadRepos(true)}
            className="w-8 h-8 rounded-full border border-[var(--line-strong)] flex items-center justify-center hover:bg-white/60 transition">
            <FiRefreshCw size={12} />
          </button>
          <span className="font-mono text-xs text-[var(--muted)]">{repos.length ? `0${activeIdx + 1} / 0${repos.length}` : "--"}</span>
        </div>
      </div>

      {/* Section title: overlaid on the left on desktop, stacked above the
          cards on mobile (there's no room for a side-by-side split there). */}
      <div className={
        isMobile
          ? "absolute left-6 right-6 top-36 pointer-events-none z-[20]"
          : "absolute left-6 md:left-16 top-1/2 -translate-y-1/2 pointer-events-none z-[20] max-w-[38vw]"
      }>
        <h2 className="font-serif leading-[0.85] tracking-tight text-[clamp(2.5rem,6vw,5.5rem)] text-[var(--ink)]">
          Selected<br /><span className="italic text-[var(--accent-2)]">projects.</span>
        </h2>
        <p className="mt-5 max-w-sm text-sm text-[var(--muted)] leading-relaxed">
          Pulled live from GitHub. Scroll to explore each project. Case studies span business analysis, forecasting and decision analytics.
        </p>
        <p className="mt-4 font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--muted)] flex items-center gap-2">
          <FiArrowRight /> Scroll to explore
        </p>
      </div>

      {/* Left-edge fade mask so cards don't visibly slide behind the title.
          Skipped on mobile: the offset there is just a small margin, not a
          text column, so a wide fade would eat into the single visible card. */}
      {!isMobile && (
        <div className="absolute inset-y-0 left-0 z-[15] pointer-events-none"
             style={{
               width: `${viewportOffset}px`,
               background: `linear-gradient(to right, var(--paper) 0%, var(--paper) 60%, rgba(245,242,236,0.85) 78%, rgba(245,242,236,0.4) 92%, transparent 100%)`,
             }}
        />
      )}

      {/* Right-edge fade mask on mobile: the next card peeks in at the edge
          to hint there's more to scroll, but its border was showing as a
          harsh line right at the screen edge. Fade it out instead. */}
      {isMobile && (
        <div className="absolute inset-y-0 right-0 z-[15] pointer-events-none w-10"
             style={{
               background: `linear-gradient(to left, var(--paper) 0%, rgba(245,242,236,0.7) 40%, transparent 100%)`,
             }}
        />
      )}

      {/* Cards: horizontal track, positioned below the stacked title on
          mobile so it doesn't overlap. */}
      <div className={`absolute ${isMobile ? "top-[46%]" : "inset-0"} left-0 right-0 bottom-0 flex items-center overflow-hidden z-[10]`}
           style={{ paddingLeft: `${viewportOffset}px` }}>
        <div className="flex will-change-transform"
             style={{ gap: `${gap}px`, transform: `translateX(${tx}px)`, transition: "transform 0.15s linear" }}>
          {loading && (
            <div className="rounded-2xl border border-[var(--line-strong)] flex items-center justify-center text-[var(--muted)]"
                 style={{ width: `${cardWidth}px`, height: isMobile ? "42vh" : "64vh" }}>
              Loading GitHub…
            </div>
          )}
          {!loading && !Array.isArray(repos) && (
            <div className="rounded-2xl border border-[var(--line-strong)] flex items-center justify-center text-[var(--muted)]"
                 style={{ width: `${cardWidth}px`, height: isMobile ? "42vh" : "64vh" }}>
              Failed to load projects.
            </div>
          )}
          {Array.isArray(repos) && repos.map((r, i) => (
            <a key={r.name} href={r.html_url} target="_blank" rel="noopener noreferrer"
               data-testid={`project-card-${i}`}
               style={{ width: `${cardWidth}px`, height: isMobile ? "42vh" : "64vh" }}
               className="group shrink-0 rounded-2xl border border-[var(--line-strong)] bg-white/80 backdrop-blur p-4 md:p-8 flex flex-col justify-between hover:bg-white hover:-translate-y-1 transition-all overflow-hidden">
              <div>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[9px] md:text-[10px] tracking-[0.25em] uppercase text-[var(--muted)]">0{i + 1}</span>
                  {r.language && (
                    <span className="flex items-center gap-1.5 font-mono text-[9px] md:text-[10px] text-[var(--muted)]">
                      <span className="w-2 h-2 rounded-full" style={{ background: LANG_COLORS[r.language] || "#9ca3af" }} />
                      {r.language}
                    </span>
                  )}
                </div>
                <h3 className="mt-2 md:mt-6 font-serif text-lg md:text-[28px] leading-tight">{r.name.replace(/-/g, " ")}</h3>
                <p className="mt-1.5 md:mt-4 text-[12px] md:text-sm text-[var(--muted)] leading-snug md:leading-relaxed line-clamp-3 md:line-clamp-6">{r.description || "Analytics case study."}</p>
              </div>

              <div>
                <div className="flex flex-wrap gap-1 md:gap-1.5 mb-2 md:mb-6">
                  {(r.topics || []).slice(0, isMobile ? 3 : 5).map(t => (
                    <span key={t} className="px-1.5 py-0.5 md:px-2 md:py-1 rounded-full text-[8px] md:text-[10px] font-mono uppercase tracking-[0.1em] md:tracking-[0.15em] border border-[var(--line)] text-[var(--muted)]">{t}</span>
                  ))}
                </div>
                <div className="flex items-center justify-between border-t border-[var(--line)] pt-1.5 md:pt-4">
                  <span className="font-mono text-[9px] md:text-[10px] tracking-[0.25em] uppercase text-[var(--muted)]">
                    {new Date(r.updated_at).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[12px] md:text-sm font-medium group-hover:text-[var(--accent-2)]">View <FiArrowUpRight /></span>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>

      {/* Progress bar */}
      <div className="absolute bottom-8 left-0 right-0 flex justify-center items-center gap-3 z-10 px-6">
        <FiArrowLeft className="opacity-40" size={14} />
        <div className="flex-1 max-w-md h-[2px] bg-[var(--line)] rounded-full overflow-hidden">
          <div className="h-full bg-[var(--accent-2)] transition-all" style={{ width: `${horizontalProgress * 100}%` }} />
        </div>
        <FiArrowRight size={14} />
      </div>
    </section>
  );
}
